'use client'

import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'

// Store node positions - spread out more horizontally
const stores = [
  { id: 'a', x: 12, y: 22, label: 'Store A' },
  { id: 'b', x: 88, y: 18, label: 'Store B' },
  { id: 'c', x: 50, y: 45, label: 'Your Store' },
  { id: 'd', x: 8, y: 72, label: 'Store C' },
  { id: 'e', x: 92, y: 76, label: 'Store D' },
  { id: 'f', x: 50, y: 82, label: 'Store E' },
]

// Connection lines between stores
const connections = [
  { from: 'a', to: 'c' },
  { from: 'b', to: 'c' },
  { from: 'c', to: 'd' },
  { from: 'c', to: 'e' },
  { from: 'c', to: 'f' },
  { from: 'a', to: 'd' },
  { from: 'b', to: 'e' },
]

// Floating items along paths - mix of packages, t-shirts, and messages
const floatingItems = [
  { from: 'a', to: 'c', delay: 0, type: 'package' },
  { from: 'b', to: 'c', delay: 1.2, type: 'tshirt' },
  { from: 'c', to: 'd', delay: 2.4, type: 'message' },
  { from: 'c', to: 'e', delay: 3.6, type: 'tshirt' },
  { from: 'c', to: 'f', delay: 4.8, type: 'package' },
  { from: 'd', to: 'a', delay: 2, type: 'message' },
  { from: 'e', to: 'b', delay: 4, type: 'package' },
]

function getStoreById(id: string) {
  return stores.find((s) => s.id === id)!
}

function StoreIcon({ x, y, isCenter, color }: { x: number; y: number; isCenter?: boolean; color: string }) {
  // SVG store/shop icon
  return (
    <g>
      {/* Store roof */}
      <path
        d={`M${x - 4},${y - 1} L${x},${y - 4} L${x + 4},${y - 1} Z`}
        fill={color}
      />
      {/* Store body */}
      <rect
        x={x - 3.5}
        y={y - 1}
        width="7"
        height="5"
        fill={color}
      />
      {/* Store door */}
      <rect
        x={x - 1}
        y={y + 1}
        width="2"
        height="3"
        fill={isCenter ? '#059669' : 'white'}
      />
      {/* Store window */}
      <rect
        x={x + 1.5}
        y={y}
        width="1.5"
        height="1.5"
        fill={isCenter ? '#059669' : 'white'}
      />
    </g>
  )
}

function StoreNode({
  x,
  y,
  label,
  isCenter,
}: {
  x: number
  y: number
  label: string
  isCenter?: boolean
}) {
  return (
    <g>
      {/* Glow effect for center store */}
      {isCenter && (
        <circle
          cx={x}
          cy={y}
          r="12"
          fill="url(#centerGlow)"
          opacity="0.5"
        />
      )}

      {/* Store background circle */}
      <circle
        cx={x}
        cy={y}
        r="8"
        fill={isCenter ? '#10B981' : 'white'}
        stroke={isCenter ? '#059669' : '#10B981'}
        strokeWidth="1"
      />

      {/* Store icon */}
      <StoreIcon
        x={x}
        y={y}
        isCenter={isCenter}
        color={isCenter ? 'white' : '#10B981'}
      />

      {/* Label */}
      <text
        x={x}
        y={y + 15}
        textAnchor="middle"
        fill="#57534e"
        fontSize="4"
        fontWeight="500"
        fontFamily="system-ui"
      >
        {label}
      </text>
    </g>
  )
}

function ConnectionLine({
  from,
  to,
}: {
  from: { x: number; y: number }
  to: { x: number; y: number }
}) {
  return (
    <line
      x1={from.x}
      y1={from.y}
      x2={to.x}
      y2={to.y}
      stroke="#10B981"
      strokeWidth="1"
      strokeDasharray="3 2"
      strokeOpacity="0.4"
    />
  )
}

function FloatingPackage() {
  return (
    <g>
      {/* Package box */}
      <rect x={-3} y={-3} width="6" height="6" rx="1" fill="#10B981" />
      {/* Package ribbon */}
      <line x1="0" y1="-3" x2="0" y2="3" stroke="white" strokeWidth="0.6" />
      <line x1="-3" y1="0" x2="3" y2="0" stroke="white" strokeWidth="0.6" />
    </g>
  )
}

function FloatingTshirt() {
  return (
    <g>
      {/* T-shirt shape */}
      <path
        d="M-3,-2 L-1.5,-3 L0,-2 L1.5,-3 L3,-2 L2,-1 L2,3 L-2,3 L-2,-1 Z"
        fill="#10B981"
      />
      {/* Collar */}
      <path
        d="M-1,-2 L0,-1 L1,-2"
        fill="none"
        stroke="white"
        strokeWidth="0.5"
      />
    </g>
  )
}

function FloatingMessage() {
  return (
    <g>
      {/* Message bubble */}
      <path
        d="M-3,-2 L3,-2 Q4,-2 4,-1 L4,1 Q4,2 3,2 L0,2 L-1,3.5 L-1,2 L-3,2 Q-4,2 -4,1 L-4,-1 Q-4,-2 -3,-2 Z"
        fill="#10B981"
      />
      {/* Message lines */}
      <line x1="-2" y1="-0.5" x2="2" y2="-0.5" stroke="white" strokeWidth="0.5" />
      <line x1="-2" y1="0.5" x2="1" y2="0.5" stroke="white" strokeWidth="0.5" />
    </g>
  )
}

function FloatingItem({
  from,
  to,
  delay,
  type,
  isInView,
}: {
  from: { x: number; y: number }
  to: { x: number; y: number }
  delay: number
  type: 'package' | 'tshirt' | 'message'
  isInView: boolean
}) {
  return (
    <motion.g
      initial={{ opacity: 0, x: from.x, y: from.y }}
      animate={isInView ? {
        opacity: [0, 1, 1, 0],
        x: [from.x, to.x],
        y: [from.y, to.y],
      } : { opacity: 0, x: from.x, y: from.y }}
      transition={{
        duration: 2.5,
        delay,
        repeat: Infinity,
        repeatDelay: 4,
        ease: 'easeInOut',
      }}
    >
      {type === 'package' && <FloatingPackage />}
      {type === 'tshirt' && <FloatingTshirt />}
      {type === 'message' && <FloatingMessage />}
    </motion.g>
  )
}

export function BrandPartnershipsIllustration() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  return (
    <div ref={ref} className="w-full h-full flex items-center justify-center p-2">
      <motion.svg
        viewBox="0 0 100 100"
        className="w-full h-full"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <defs>
          {/* Gradient for center glow */}
          <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
          </radialGradient>

          {/* Background gradient */}
          <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#D1FAE5" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#ECFDF5" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#D1FAE5" stopOpacity="0.3" />
          </linearGradient>
        </defs>

        {/* Background */}
        <rect width="100" height="100" fill="url(#bgGradient)" rx="8" />

        {/* Connection lines - static */}
        {connections.map((conn) => {
          const from = getStoreById(conn.from)
          const to = getStoreById(conn.to)
          return (
            <ConnectionLine
              key={`${conn.from}-${conn.to}`}
              from={{ x: from.x, y: from.y }}
              to={{ x: to.x, y: to.y }}
            />
          )
        })}

        {/* Store nodes - static */}
        {stores.map((store) => (
          <StoreNode
            key={store.id}
            x={store.x}
            y={store.y}
            label={store.label}
            isCenter={store.id === 'c'}
          />
        ))}

        {/* Floating items - these animate */}
        {floatingItems.map((item, i) => {
          const from = getStoreById(item.from)
          const to = getStoreById(item.to)
          return (
            <FloatingItem
              key={`item-${i}`}
              from={{ x: from.x, y: from.y }}
              to={{ x: to.x, y: to.y }}
              delay={item.delay}
              type={item.type as 'package' | 'tshirt' | 'message'}
              isInView={isInView}
            />
          )
        })}
      </motion.svg>
    </div>
  )
}
